
import { useState, useMemo, useEffect } from 'react';
import { Trip } from '@/types/itinerary';
import { TripFilters } from './tripTypes';

export const useTripFilters = (trips: Trip[]) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredTrips, setFilteredTrips] = useState<Trip[]>([]);
  
  // Initialize filters
  const [filters, setFilters] = useState<TripFilters>({
    status: 'all',
    startDate: null,
    endDate: null
  });
  
  // Calculate active filter count for UI
  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (filters.status !== 'all') count++;
    if (filters.startDate) count++;
    if (filters.endDate) count++;
    return count;
  }, [filters]);

  // Reset filters to default values
  const resetFilters = () => {
    setFilters({
      status: 'all',
      startDate: null,
      endDate: null
    });
  };
  
  // Effect to filter trips based on search term and filters
  useEffect(() => {
    if (trips.length === 0) {
      setFilteredTrips([]);
      return;
    }
    
    // Create a new Date at midnight for date comparisons
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    // First filter by search term
    let filtered = trips;
    
    if (searchTerm.trim() !== '') {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(trip => 
        trip.name.toLowerCase().includes(term) || 
        (trip.destination && trip.destination.toLowerCase().includes(term)) ||
        (trip.description && trip.description.toLowerCase().includes(term))
      );
    }
    
    // Then apply other filters
    if (filters.status !== 'all') {
      filtered = filtered.filter(trip => {
        const startDate = new Date(trip.start_date);
        const endDate = new Date(trip.end_date);
        
        switch (filters.status) {
          case 'upcoming':
            return startDate > today;
          case 'ongoing':
            return startDate <= today && endDate >= today;
          case 'past':
            return endDate < today;
          default:
            return true;
        }
      });
    }
    
    // Apply date range filters if set
    if (filters.startDate) {
      filtered = filtered.filter(trip => {
        const tripStartDate = new Date(trip.start_date);
        return tripStartDate >= filters.startDate!;
      });
    }
    
    if (filters.endDate) {
      filtered = filtered.filter(trip => {
        const tripEndDate = new Date(trip.end_date);
        return tripEndDate <= filters.endDate!;
      });
    }
    
    setFilteredTrips(filtered);
  }, [searchTerm, trips, filters]);

  return {
    filteredTrips,
    searchTerm,
    setSearchTerm,
    filters,
    setFilters,
    resetFilters,
    activeFilterCount
  };
};
